import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';

@Component({
  selector: 'app-slider',
  standalone: true,
  imports: [],
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderComponent implements OnDestroy {
  min = input(0);
  max = input(100);
  step = input(1);
  range = input(false);
  width = input('200px');
  value = input<number | [number, number]>(0);

  valueChange = output<number | [number, number]>();

  private isDragging = false;
  private isEndDragging = false;
  private internalValue = signal<number | [number, number] | null>(null);
  private readonly onMouseMoveRef = this.onMouseMove.bind(this);
  private readonly onMouseUpRef = this.onMouseUp.bind(this);

  private currentValue = computed(() => this.internalValue() ?? this.value());

  handlePosition = computed(() => {
    const val = this.currentValue();
    if (this.range() && Array.isArray(val)) {
      return ((val[0] - this.min()) / (this.max() - this.min())) * 100;
    }
    const numValue = Array.isArray(val) ? val[0] : val;
    return ((numValue - this.min()) / (this.max() - this.min())) * 100;
  });

  endHandlePosition = computed(() => {
    const val = this.currentValue();
    if (this.range() && Array.isArray(val)) {
      return ((val[1] - this.min()) / (this.max() - this.min())) * 100;
    }
    return 0;
  });

  fillWidth = computed(() => {
    if (this.range() && Array.isArray(this.currentValue())) {
      return this.endHandlePosition() - this.handlePosition();
    }
    return this.handlePosition();
  });

  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    document.addEventListener('mousemove', this.onMouseMoveRef);
    document.addEventListener('mouseup', this.onMouseUpRef);
    event.preventDefault();
  }

  onEndMouseDown(event: MouseEvent): void {
    this.isEndDragging = true;
    document.addEventListener('mousemove', this.onMouseMoveRef);
    document.addEventListener('mouseup', this.onMouseUpRef);
    event.preventDefault();
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isDragging && !this.isEndDragging) return;

    const rect = (event.target as Element)?.closest('.slider')?.getBoundingClientRect();
    if (!rect) return;

    const percentage = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
    const newValue = this.min() + (percentage / 100) * (this.max() - this.min());
    const steppedValue = Math.round(newValue / this.step()) * this.step();

    const currentVal = this.currentValue();
    let newVal: number | [number, number];
    if (this.range() && Array.isArray(currentVal)) {
      if (this.isDragging) {
        newVal = [steppedValue, currentVal[1]];
      } else {
        newVal = [currentVal[0], steppedValue];
      }
    } else {
      newVal = steppedValue;
    }

    this.internalValue.set(newVal);
    this.valueChange.emit(newVal);
  }

  private onMouseUp(): void {
    this.isDragging = false;
    this.isEndDragging = false;
    this.internalValue.set(null);
    document.removeEventListener('mousemove', this.onMouseMoveRef);
    document.removeEventListener('mouseup', this.onMouseUpRef);
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.onMouseMoveRef);
    document.removeEventListener('mouseup', this.onMouseUpRef);
  }

  onKeyDown(event: KeyboardEvent): void {
    const currentVal = this.currentValue();
    const currentNumValue = Array.isArray(currentVal) ? currentVal[0] : currentVal;

    let newVal: number;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        newVal = Math.max(this.min(), currentNumValue - this.step());
        this.internalValue.set(newVal);
        this.valueChange.emit(newVal);
        event.preventDefault();
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        newVal = Math.min(this.max(), currentNumValue + this.step());
        this.internalValue.set(newVal);
        this.valueChange.emit(newVal);
        event.preventDefault();
        break;
    }
  }
}
